/* eslint-disable no-param-reassign */
import {
  FormInputElement, FormInputElementWithOptions, FormInputType, FormInputTypesWithOptions, IForm,
} from '@typedream/core';
import { DreamWithOverride } from '../../plate';
import { ELEMENT_FORM_INPUT } from '../../pluginKeys';
import { normalizeFormInputName } from './normalizers/normalizeFormInputName';
import { normalizeFormInputOptions } from './normalizers/normalizeFormInputOptions';
import { FormEditor, FormInputPluginOptions } from './types';
// ********************************************************************************
// START Utility Types
// We might want to move some of these to the @typedream/data
// ********************************************************************************
type ValueOf<T> = T[keyof T];

type FormSchema = IForm['schema'];

type inputTypeWithoutOptions = Exclude<FormInputType, typeof FormInputTypesWithOptions[number]>;

type InputOptionsSchema = Exclude<ValueOf<FormSchema>, { type: inputTypeWithoutOptions }>;
// ********************************************************************************
// Utility Types END
// ********************************************************************************

function isInputTypeWithOptions(s: ValueOf<FormSchema>): s is InputOptionsSchema {
  // if s.type is included in FormInputTypeWithOptions return true  else return false
  return (FormInputTypesWithOptions as readonly string[]).includes(s.type as string);
}

function isNodeFormInputWithOptions(s: FormInputElement): s is FormInputElementWithOptions {
  return s.type === 'form_input' && (s.inputType === 'multiselection' || s.inputType === 'selection');
}

export const withFormInput: DreamWithOverride<FormInputPluginOptions> = (editor, plugin) => {
  const e = editor as FormEditor;
  const { normalizeNode } = e;

  const { initialFormData } = plugin.options;

  // eslint-disable-next-line no-param-reassign
  e.initialLabelToNameMapping = Object.entries(
    initialFormData.schema || {},
  ).reduce((acc, currVal) => {
    // skip deleted name
    if (currVal[1].deleted) return acc;
    return {
      ...acc,
      [currVal[1].name]: currVal[0],
    };
  }, {});

  // eslint-disable-next-line no-param-reassign
  // flip the map of e.initialLabelToNameMapping
  e.currentInputNameToLabelMapping = Object.entries(
    e.initialLabelToNameMapping,
  ).reduce((acc, currVal) => ({
    ...acc,
    [currVal[1]]: currVal[0],
  }), {});

  e.currentInputNameToBlockIDMapping = {};

  e.initialNameToOptionsMap = Object.entries(
    initialFormData.schema || {},
  ).reduce((acc, currVal) => {
    // skip deleted name
    if (currVal[1].deleted) return acc;

    if (!isInputTypeWithOptions(currVal[1])) {
      return acc;
    }
    return {
      ...acc,
      [currVal[0]]: { ...(currVal[1].options || {}) },
    };
  }, {});

  e.currentNameToOptionsMap = { ...e.initialNameToOptionsMap };

  e.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (
      node.type === ELEMENT_FORM_INPUT
      && normalizeFormInputName(e, [node as FormInputElement, path])
    ) {
      return;
    }

    if (
      node.type === ELEMENT_FORM_INPUT
      && isNodeFormInputWithOptions(node as FormInputElement)
      && !!node.name
      && normalizeFormInputOptions(e, [node as FormInputElementWithOptions, path])
    ) {
      return;
    }

    normalizeNode(entry);
  };

  return e;
};
